import { JsonViewer } from '@/components/JsonViewer';
import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { allBrandsState, currentUserIdState, recallMatchesState, recallState, recallVerdictsState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import logger from '@/util/logger';
import { RecallChangedError } from '@rock/dao';
import { BrandAliasDao, RecallDao } from '@rock/db-admin';
import {
    isRuleActive,
    RECALL_TOPIC_FOLLOW_MAX_YEARS,
    type RecallReachesNobodyReason,
    recallReachesNobody,
} from '@rock/recalls';
import type {
    Brand,
    BrandAliasKey,
    BrandId,
    Recall,
    RecallId,
    RecallRejectReason,
    Timestamp,
    UserId,
} from '@rock/types';
import { recallKey, RecallRejectReasonSchema, UserIdSchema } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import { useAtomValue } from 'jotai';
import { ExternalLink } from 'lucide-react';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RecallAiResultsTab } from './RecallAiResultsTab';
import { RecallHeadline } from './RecallHeadline';
import { RecallHistoryTab } from './RecallHistoryTab';
import { liveBrandIn } from './recallBrands';
import { RecallMatchesTab } from './RecallMatchesTab';
import { RecallRulesTab } from './RecallRulesTab';
import { PROCESS_COST_PER_RECALL_USD, requestRecallJob } from './recallJobs';
import { RECALL_STATUS_META } from './recallStatus';
import { PageUnavailableBadge } from './recallTags';

const log = logger('RecallDetailsPage');
const recallDao = new RecallDao();
const brandAliasDao = new BrandAliasDao();

const REJECT_REASON_LABELS: Record<RecallRejectReason, string> = {
    'not-ours': 'Not our kind of product',
    duplicate: 'Duplicate of another recall',
    'wrong-rules': 'The rules can’t be made right',
    other: 'Other',
};

/** Why Approve refuses a recall that would reach nobody, by `recallReachesNobody`'s reason. */
const REACHES_NOBODY: Record<RecallReachesNobodyReason, string> = {
    'no-model-rule':
        'It has no topic and its rules match no model. Add a topic, add or accept a model number, or Match Any Model on the Rules tab when the notice names none.',
    'no-brand':
        'It has no topic and none of its brands resolves to a catalog brand, so it matches nothing. Add a topic or resolve a brand on the Rules tab.',
};

/**
 * One recall: the notice with its identifiers lit, the merged rules as reviewable chips, and the actions
 * that move it — Approve (showing the Impact Preview), Reject, Reopen, Process / Re-process, each confirmed in a
 * dialog, and Reclassify. The invariant: nothing a homeowner can see exists until Approve, and Approve is per
 * recall.
 */
export const RecallDetailsPage: FC = () => {
    const { recallId: rawId } = useParams<{ recallId: string }>();
    const recallId = useMemo(() => (rawId ? (decodeURIComponent(rawId) as RecallId) : undefined), [rawId]);
    const [recall, loadable] = useLoadableAtomValue(useMemo(() => recallState(recallId), [recallId]));
    const matches = useAsyncAtomValue(
        useMemo(() => recallMatchesState(recallId), [recallId]),
        [],
    );
    const verdicts = useAsyncAtomValue(
        useMemo(() => recallVerdictsState(recallId), [recallId]),
        [],
    );
    const brands = useAsyncAtomValue(allBrandsState, []);
    const rawUserId = useAtomValue(currentUserIdState);
    const userId = useMemo(() => (rawUserId ? UserIdSchema.parse(rawUserId) : undefined), [rawUserId]);
    const [busy, setBusy] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reopenOpen, setReopenOpen] = useState(false);
    const [processOpen, setProcessOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState<RecallRejectReason>('not-ours');
    const [rejectNote, setRejectNote] = useState('');

    const basePath = recallId ? `/products/recalls/${encodeURIComponent(recallId)}` : '/products/recalls/catalog';

    /** Wrap a write with the busy flag and toasts; resolves whether it succeeded. */
    const run = async (label: string, work: () => Promise<void>, success: string): Promise<boolean> => {
        setBusy(true);
        try {
            await work();
            toast.success(success);
            return true;
        } catch (err) {
            if (err instanceof RecallChangedError) {
                toast.error('This recall changed while you were looking at it', {
                    description: 'The page has the latest version now — check it and try again.',
                });
            } else {
                log.error(`Failed to ${label}`, { err, recallId });
                toast.error(`Unable to ${label}`, {
                    description: err instanceof Error ? err.message : 'Unexpected error.',
                });
            }
            return false;
        } finally {
            setBusy(false);
        }
    };

    if (!recall) {
        return (
            <PageShell>
                <PageHeader title="Recall" id={recallId} backLabel="Back to recalls" />
                <Card>
                    <CardHeader>
                        <CardTitle>{loadable.state === 'loading' ? 'Loading recall…' : 'Recall not found'}</CardTitle>
                        <CardDescription>
                            {loadable.state === 'loading'
                                ? 'Fetching the recall record.'
                                : 'No recall document at this id.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            </PageShell>
        );
    }

    const meta = RECALL_STATUS_META[recall.status];
    const reviewing = recall.draftRules ?? recall.rules;
    const topicIds = recall.classification?.topicIds ?? [];
    // Brands are resolved again on Approve, so only the rules and the topics can rule an approval out ahead of it.
    const hasModelRule = !!reviewing?.modelRules.some(isRuleActive);
    const reachesNobody = !topicIds.length && !hasModelRule;
    // The Topics tab's rule (`topicApprovalBlock`): a typed recall goes live once its rules can be reviewed.
    const awaitingRules = recall.typed && !reviewing;
    const canApprove =
        !reachesNobody &&
        !awaitingRules &&
        !!userId &&
        (recall.status === 'draft' ||
            recall.status === 'classified' ||
            (recall.status === 'approved' && !!recall.draftRules));
    const canReject = !!userId && recall.status !== 'rejected';
    const canReopen = !!userId && (recall.status === 'approved' || recall.status === 'rejected');
    const canProcess = !!userId && recall.status !== 'new';

    /**
     * Approve against the brands the catalog resolves NOW: a brand request queued at processing time may have
     * minted the brand since. The gate rides in the approval's own write.
     */
    const approve = async (user: UserId) => {
        const resolved = await resolveBrandIds(recall, brands);
        const gap = recallReachesNobody({ brandIds: resolved, rules: reviewing, topicIds });
        if (gap) throw new Error(REACHES_NOBODY[gap]);
        await recallDao.approve(recall, user, new Date().toISOString() as Timestamp, resolved);
    };

    const requestJob = (kind: 'process' | 'reclassify', user: UserId) =>
        requestRecallJob(
            kind === 'process' ? { kind, recallIds: [recall.id] } : { kind, recallIds: [recall.id] },
            user,
        );

    return (
        <PageShell>
            <PageHeader title={recall.title} id={recall.id} backLabel="Back to recalls" />

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Badge variant={meta.variant} className={meta.className}>
                                {meta.label}
                            </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{meta.hint}</TooltipContent>
                    </Tooltip>
                    {recall.draftRules && <Badge variant="outline">re-review pending</Badge>}
                    {recall.pageUnavailable && <PageUnavailableBadge pageUnavailable={recall.pageUnavailable} />}
                    <a
                        href={recall.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
                    >
                        {recallKey(recall.source, recall.sourceId)} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <ActionButton
                        hint={
                            reachesNobody
                                ? REACHES_NOBODY['no-model-rule']
                                : awaitingRules
                                  ? 'Its rules are still being extracted. Approve once they arrive, so they are reviewed with it.'
                                  : hasModelRule
                                    ? 'Make the rules live: matching units get a verdict and their owners are notified. Shows the impact first.'
                                    : 'Make it live: homes following its topics get it under FYI Only. Nobody is notified.'
                        }
                        disabled={!canApprove || busy}
                        onClick={() => setApproveOpen(true)}
                        variant="default"
                    >
                        {recall.draftRules ? 'Approve re-process…' : 'Approve…'}
                    </ActionButton>
                    <ActionButton
                        hint="Keep the recall on file but match nothing. Asks for a reason first."
                        disabled={!canReject || busy}
                        onClick={() => setRejectOpen(true)}
                        className="text-destructive hover:text-destructive"
                    >
                        Reject…
                    </ActionButton>
                    <ActionButton
                        hint="Send it back to review. Asks first."
                        disabled={!canReopen || busy}
                        onClick={() => setReopenOpen(true)}
                    >
                        Reopen…
                    </ActionButton>
                    <ActionButton
                        hint={`${recall.rules ? 'Run both extractors over the notice again' : 'Extract matching rules with both extractors'}, about $${PROCESS_COST_PER_RECALL_USD.toFixed(2)}. Asks first.`}
                        disabled={!canProcess || busy}
                        onClick={() => setProcessOpen(true)}
                    >
                        {recall.rules ? 'Re-process…' : 'Process…'}
                    </ActionButton>
                    <ActionButton
                        hint="Re-run the type classifier now, for a fraction of a cent. Types and a headline an admin edited are kept."
                        disabled={!userId || busy}
                        onClick={() =>
                            userId &&
                            run(
                                'request reclassify',
                                async () => {
                                    await requestJob('reclassify', userId);
                                },
                                'Reclassify requested',
                            )
                        }
                    >
                        Reclassify
                    </ActionButton>
                </div>
            </div>
            {recall.classification && (
                <RecallHeadline
                    key={recall.id}
                    headline={recall.classification.headline}
                    adminEdited={!!recall.classification.headlineAdminEdited}
                    disabled={busy}
                    onSave={(headline) =>
                        run(
                            'update the headline',
                            () => recallDao.setHeadline(recall, headline, new Date().toISOString() as Timestamp),
                            'Headline saved',
                        )
                    }
                />
            )}

            <TabNav
                tabs={[
                    { label: 'Rules', to: `${basePath}/rules` },
                    { label: 'Matches', to: `${basePath}/matches`, count: recall.dryRun?.modelIds.length },
                    { label: 'AI Results', to: `${basePath}/ai`, count: recall.aiCalls.length },
                    { label: 'History', to: `${basePath}/history` },
                ]}
            />
            <Routes>
                <Route index element={<Navigate replace to="./rules" />} />
                <Route path="rules" element={<RecallRulesTab recall={recall} />} />
                <Route
                    path="matches"
                    element={<RecallMatchesTab recall={recall} matches={matches} verdicts={verdicts} />}
                />
                <Route path="ai" element={<RecallAiResultsTab recall={recall} />} />
                <Route path="history" element={<RecallHistoryTab recall={recall} />} />
            </Routes>

            <Card>
                <CardHeader>
                    <CardTitle>Object</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={recall} />
                    </div>
                </CardContent>
            </Card>

            <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Approve this recall?</AlertDialogTitle>
                        <AlertDialogDescription>{approvePreview(recall)}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() =>
                                userId && run('approve', () => approve(userId), 'Recall approved: it is live')
                            }
                        >
                            Approve
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reopen this recall?</AlertDialogTitle>
                        <AlertDialogDescription>{reopenPreview(recall)}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() =>
                                userId &&
                                run(
                                    'reopen',
                                    () => recallDao.reopen(recall, userId, new Date().toISOString() as Timestamp),
                                    'Recall reopened for review',
                                )
                            }
                        >
                            Reopen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={processOpen} onOpenChange={setProcessOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {recall.rules ? 'Re-process this recall?' : 'Process this recall?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{processPreview(recall)}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() =>
                                userId &&
                                run(
                                    'request processing',
                                    async () => {
                                        await requestJob('process', userId);
                                    },
                                    `${recall.rules ? 'Re-process' : 'Processing'} requested`,
                                )
                            }
                        >
                            {recall.rules ? 'Re-process' : 'Process'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={rejectOpen} onOpenChange={(open) => !busy && setRejectOpen(open)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reject this recall</DialogTitle>
                        <DialogDescription>
                            The recall stays on file and filterable but matches nothing.
                            {recall.status === 'approved'
                                ? ' Every unit and FYI home it reached is withdrawn; no new notification is sent.'
                                : ''}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-3 py-2">
                        <Select
                            value={rejectReason}
                            onValueChange={(value) => setRejectReason(RecallRejectReasonSchema.parse(value))}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {RecallRejectReasonSchema.options.map((reason) => (
                                    <SelectItem key={reason} value={reason}>
                                        {REJECT_REASON_LABELS[reason]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Textarea
                            placeholder="Note (optional)"
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={busy || !userId}
                            onClick={async () => {
                                if (!userId) return;
                                const ok = await run(
                                    'reject',
                                    () =>
                                        recallDao.reject(
                                            recall,
                                            {
                                                reason: rejectReason,
                                                note: rejectNote.trim() || undefined,
                                                reviewedBy: userId,
                                            },
                                            new Date().toISOString() as Timestamp,
                                        ),
                                    'Recall rejected',
                                );
                                if (ok) setRejectOpen(false);
                            }}
                        >
                            Reject
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageShell>
    );
};

function approvePreview(recall: Recall): string {
    const rules = recall.draftRules ?? recall.rules;
    const fyi = topicsPreview(recall);
    if (!rules?.modelRules.some(isRuleActive)) return fyi;
    const unverified = rules?.modelRules.filter((rule) => !rule.verified && !rule.accepted).length ?? 0;
    const dryRun = recall.dryRun;
    const effect = dryRun
        ? `${dryRun.modelIds.length} model(s), ${dryRun.unitCount} unit(s) in ${dryRun.homeCount} home(s) would be evaluated and their owners notified.`
        : 'No Impact Preview on file; matching units will be evaluated when the rules are applied.';
    const stale =
        dryRun && dryRun.at < recall.updatedAt
            ? ` That Impact Preview is from ${formatRelative(dryRun.at)}, before the latest edit; recalculate it on the Matches tab.`
            : '';
    const warning = unverified ? ` ${unverified} unverified identifier(s) stay out of matching until accepted.` : '';
    const fyiLine = recall.classification?.topicIds.length ? ` ${fyi}` : '';
    return `${effect}${stale}${warning} Later matches — units added after today — are evaluated automatically.${fyiLine}`;
}

function topicsPreview(recall: Recall): string {
    const count = recall.classification?.topicIds.length ?? 0;
    return `Every home following ${count === 1 ? 'its topic' : `any of its ${count} topics`} from before it was published gets it as an FYI; a follow reaches back up to ${RECALL_TOPIC_FOLLOW_MAX_YEARS} years. Nobody is notified.`;
}

function reopenPreview(recall: Recall): string {
    const back = recall.draftRules || recall.rules ? 'a draft' : 'classified';
    return recall.status === 'approved'
        ? `It goes back to ${back} for review. Every unit and FYI home it reached is withdrawn; no new notification is sent.`
        : `It goes back to ${back} for review. Nothing reaches a homeowner until it is approved.`;
}

function processPreview(recall: Recall): string {
    const reread = 'The notice is read fresh from the source and its web page first. ';
    const price = `about $${PROCESS_COST_PER_RECALL_USD.toFixed(2)}`;
    if (!recall.rules) {
        return `${reread}Both extractors read the notice, ${price}. The rules arrive as a draft for your review.`;
    }
    const cost = `${reread}Both extractors read the notice again, ${price}.`;
    const kept =
        'Your added identifiers, removals and edited checks carry over, an accepted identifier stays accepted when found again, and brands are only added to.';
    if (recall.status === 'approved') {
        return recall.draftRules
            ? `${cost} The new rules replace the re-process draft already waiting. ${kept} The live rules keep matching until you approve.`
            : `${cost} The new rules wait as a re-process draft for your review. ${kept} The live rules keep matching until you approve it.`;
    }
    const unreject = recall.status === 'rejected' ? ' The recall leaves Rejected and becomes a draft again.' : '';
    return `${cost} The extractors' findings are replaced. ${kept}${unreject}`;
}

/** An action with a hover explanation. The span keeps the tooltip working while the button is disabled. */
const ActionButton: FC<{
    hint: string;
    disabled: boolean;
    onClick: () => void;
    variant?: 'default' | 'outline';
    className?: string;
    children: ReactNode;
}> = ({ hint, disabled, onClick, variant = 'outline', className, children }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span className="inline-flex">
                <Button variant={variant} className={className} disabled={disabled} onClick={onClick}>
                    {children}
                </Button>
            </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{hint}</TooltipContent>
    </Tooltip>
);

/**
 * The brand ids the rules' brand names resolve to today, through the same alias projections the server
 * uses (a merged brand followed to its winner, a junk one dropped), plus whatever an admin already picked
 * by hand.
 */
async function resolveBrandIds(recall: Recall, brands: Brand[]): Promise<BrandId[]> {
    const rules = recall.draftRules ?? recall.rules;
    const current = recall.draftRules ? (recall.draftBrandIds ?? recall.brandIds) : recall.brandIds;
    const ids = new Set<BrandId>(current);
    for (const brand of rules?.brands ?? []) {
        const key = normalizeBrandName(brand.value).key;
        if (!key) continue;
        const alias = await brandAliasDao.getBrandAlias(key as BrandAliasKey);
        const live = alias?.brandId ? liveBrandIn(alias.brandId, brands) : undefined;
        if (live) ids.add(live.id);
    }
    return [...ids];
}
